import { Intent, NonIdealState, Spinner, SpinnerSize } from '../../components/ui';
import { AdjustmentsHorizontalIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { StoryCardSkeleton } from '../../components/uikit/story-card-skeleton/storyCardSkeleton';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { Redirect, useHistory, useRouteMatch } from 'react-router-dom';
import VisibilitySensor from 'react-visibility-sensor';
import { SharedLinkCard } from '../../components/common/components/shared-link-card/SharedLinkCard';
import DashboardNav from '../../components/dashboard-nav/DashboardNav';
import FilterNav, {
  SortOption,
  sortOptions,
  TimeFilter,
  timeFilters,
} from '../../components/filter-nav/filterNav';
import InactiveSubscription from '../../components/inactive-subscription/inactiveSubscription';
import LoadingSpinner from '../../components/loading-spinner/LoadingSpinner';
import PullToRefresh from '../../components/pull-to-refresh/pullToRefresh';
import { ReaderPanel } from '../../components/reader-panel/readerPanel';
import SocialPreferencesPanel from '../../components/social-preferences-panel/socialPreferencesPanel';
import StoryTweetsPanel from '../../components/story-tweets-panel/storyTweetsPanel';
import { useAccount } from '../../hooks/useAccount';
import { StoryClickContext } from '../../hooks/useBriefStories';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import useLocalStorage from '../../hooks/useLocalStorage';
import { useSharedLinks } from '../../hooks/useSharedLinks';
import DashboardPageLayout from '../../layouts/dashboard/dashboardPageLayout';
import { ExternalUserType } from '../../services/models/external-user';
import { FeatureFlag } from '../../services/models/feature-flags';
import { SharedLink } from '../../services/models/shared-link';
import { classNames } from '../../utils/class-names';

type StoryMatchParams = {
  storyId: string;
};

const SocialPage: FunctionComponent = () => {
  const history = useHistory();
  const { path, url } = useRouteMatch();
  const storyMatch = useRouteMatch<StoryMatchParams>(`${path}/story/:storyId`);

  const storyId = storyMatch?.params?.storyId;
  const sharedLinkId = Number(storyId);

  const { account: currentAccount, loading: isLoadingSession } = useAccount();
  const account = currentAccount!;
  const subscription = account?.subscription;
  const { sharedLinks, loadSharedLinksPage, loadSharedLink, markStoryClicked } = useSharedLinks();
  const twitterUser = account.externalUsers.find(eu => eu.type === ExternalUserType.TWITTER);
  const selectedSharedLink = sharedLinks.items?.find(item => item.id === sharedLinkId);
  const socialBriefFlag = useFeatureFlag(FeatureFlag.ShowSocialBrief);

  // TODO: Control via SiteFlag
  const isTwitterIntegrationActive = false;

  const [sortOption, setSortOption] = useLocalStorage('persist:sort_option', sortOptions[0]);
  const [timeFilter, setTimeFilter] = useLocalStorage('persist:time_filter', timeFilters[0]);
  const [sharesFilter, setSharesFilter] = useLocalStorage('persist:shares_filter', 0);
  const [showPreferences, setShowPreferences] = useState(false);

  const [readerAppShown, setReaderAppShown] = useState(false);
  const [readerUrl, setReaderUrl] = useState<string | null>(null);
  const [readerStoryId, setReaderStoryId] = useState<number | null>(null);

  const handleFilterOptionsChange = useCallback(
    (sort: SortOption, time: TimeFilter, shares: number) => {
      loadSharedLinksPage(0, undefined, sort.value, time.value, shares);
    },
    [loadSharedLinksPage]
  );

  const handlePullToRefresh = (): Promise<any> => {
    return loadSharedLinksPage(0, undefined, sortOption.value, timeFilter.value, sharesFilter);
  };

  const handleInfiniteScrollAction = (visible: boolean) => {
    if (visible) {
      loadSharedLinksPage(
        sharedLinks.lastLoadedCursor || 0,
        undefined,
        sortOption.value,
        timeFilter.value,
        sharesFilter
      );
    }
  };

  useEffect(() => {
    // isFetching guard: startFetching re-renders with items still undefined,
    // so without it this effect refires and issues duplicate first-page
    // requests until the first response lands.
    if (account.metadata?.isTwitterUser && !sharedLinks.items && !sharedLinks.isFetching) {
      loadSharedLinksPage(0, undefined, sortOption.value, timeFilter.value, sharesFilter);
    }
  }, [account, sharedLinks, sortOption, timeFilter, sharesFilter, loadSharedLinksPage]);

  if (isLoadingSession) {
    return <LoadingSpinner />;
  }

  if ((!socialBriefFlag || !account.metadata?.isTwitterUser) && !isLoadingSession) {
    return <Redirect to="/dashboard" />;
  }

  const isLoading = () => {
    return !sharedLinks.items || (!!sharedLinks.isFetching && !!sharedLinks.isFetchingFirstPage);
  };

  const sharedLinkToStoryCard = (sharedLink: SharedLink, index: number) => {
    if (sharedLink.story) {
      return (
        <SharedLinkCard
          key={sharedLink.id}
          sharedLink={sharedLink}
          onShareBarClick={(storyId, storyUri) => {
            history.push(`${url}/story/${storyId}`);
          }}
          onReadStory={(storyId, storyUri) => {
            setReaderStoryId(storyId);
            setReaderUrl(storyUri);
            setReaderAppShown(true);
          }}
        />
      );
    } else {
      if (!sharedLink.isFetching && !sharedLink.isLoaded) {
        loadSharedLink(sharedLink.id);
      }

      return <StoryCardSkeleton key={sharedLink.id} />;
    }
  };

  const getContent = () => {
    if (isLoading()) {
      return (
        <div className="flex h-screen items-center justify-center">
          <Spinner intent={Intent.PRIMARY} size={SpinnerSize.STANDARD} />
        </div>
      );
    }

    if (!subscription.active) {
      return (
        <InactiveSubscription
          subscription={subscription}
          title="Thank you for trying Newslit!"
          description="You've reached the end of your free trial. To access your social feed, email digests and alerts please select one of our paid plans."
        />
      );
    }

    if (!sharedLinks.items || sharedLinks.items?.length === 0) {
      return (
        <div className="flex h-screen items-center justify-center">
          <div className="p-8">
            <NonIdealState
              icon="social-media"
              title="No Recent Stories"
              description="Sorry, no stories have been shared by your network"
            />
          </div>
        </div>
      );
    }

    return (
      <div className="block sm:flex sm:h-full sm:overflow-y-auto">
        <PullToRefresh onRefresh={handlePullToRefresh}>
          <div className="social-feed-container border-t border-gray-100">
            {sharedLinks.items?.map((sharedLink, index) => {
              return sharedLinkToStoryCard(sharedLink, index);
            })}

            {sharedLinks.hasMore && getInfiniteScrollIndicator()}
          </div>
        </PullToRefresh>
      </div>
    );
  };

  const getInfiniteScrollIndicator = () => {
    return (
      <VisibilitySensor partialVisibility onChange={handleInfiniteScrollAction}>
        <>
          <StoryCardSkeleton />
          <StoryCardSkeleton />
          <StoryCardSkeleton />
        </>
      </VisibilitySensor>
    );
  };

  const renderSocialBriefOptions = () => {
    return (
      <div className="flex flex-nowrap">
        <button
          type="button"
          className="bg-white rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-600 focus:outline-hidden"
          onClick={() => setShowPreferences(true)}
        >
          <AdjustmentsHorizontalIcon className="h-5 w-5" aria-hidden="true" />
          <span className="sr-only">Social Brief adjustments</span>
        </button>
      </div>
    );
  };

  return (
    <StoryClickContext.Provider value={{ onStoryViewed: markStoryClicked }}>
      <React.StrictMode>
        <DashboardPageLayout title={`@${twitterUser?.username}`}>
          {/* main area */}
          <main className="flex-1 z-0 flex overflow-y-auto overflow-hidden focus:outline-hidden">
            {/* Primary column: Sharedlinks */}
            <section
              className={classNames(
                isLoading()
                  ? 'flex-1'
                  : storyMatch
                    ? 'hidden lg:flex lg:max-w-(--breakpoint-sm)'
                    : 'flex-1',
                'relative z-0 md:max-w-(--breakpoint-sm) overflow-y-auto focus:outline-hidden'
              )}
            >
              <div className="h-full relative flex flex-col border-r border-gray-200">
                {/* TODO: Improve our pull-to-refresh & navbbar className="sticky top-0" */}
                <div className="shrink-0 z-20">
                  <div
                    className={classNames(
                      isTwitterIntegrationActive ? 'h-20 sm:hidden' : 'h-52 sm:hidden'
                    )}
                  ></div>
                  <div className="fixed z-10 top-0 right-0 w-full sm:relative sm:top-auto sm:right-auto">
                    <DashboardNav>
                      <div className="flex flex-col shrink grow h-full items-stretch justify-center">
                        <h2 className="text-lg font-bold m-0 p-0">@{twitterUser?.username}</h2>
                      </div>
                      <div className="flex ml-4">{renderSocialBriefOptions()}</div>
                    </DashboardNav>

                    <FilterNav
                      sortOptions={sortOptions}
                      activeSortOption={sortOption}
                      onActiveSortChange={setSortOption}
                      timeFilterOptions={timeFilters}
                      activeTimeFilter={timeFilter}
                      onActiveTimeFilterChange={setTimeFilter}
                      activeSharesFilter={sharesFilter}
                      onActiveSharesFilterChange={setSharesFilter}
                      onFilterOptionsChange={handleFilterOptionsChange}
                    />

                    {!isTwitterIntegrationActive && (
                      <div className="overflow-hidden bg-yellow-50 sm:rounded-lg">
                        <div className="px-4 py-5 sm:p-6">
                          <div className="flex">
                            <div className="shrink-0">
                              <ExclamationTriangleIcon
                                className="h-5 w-5 text-yellow-400"
                                aria-hidden="true"
                              />
                            </div>
                            <div className="ml-3">
                              <p className="text-sm text-yellow-700">
                                Our Twitter integration has been disabled. We are actively trying to
                                resolve this and will share updates as additional information
                                becomes available.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {getContent()}
              </div>
            </section>

            {/* secondary column: Sharedlink Tweets */}
            {!isLoading() && storyMatch && selectedSharedLink && (
              <aside className="min-w-0 h-full flex flex-col lg:shrink-0 lg:max-w-(--breakpoint-sm) xl:max-w-md overflow-hidden border-r border-gray-200">
                <StoryTweetsPanel sharedLink={selectedSharedLink} />
              </aside>
            )}
          </main>

          <SocialPreferencesPanel
            panelOpen={showPreferences}
            onClose={() => setShowPreferences(false)}
          />

          <ReaderPanel
            url={readerUrl}
            storyId={readerStoryId}
            show={readerAppShown}
            onClose={setReaderAppShown}
          />
        </DashboardPageLayout>
      </React.StrictMode>
    </StoryClickContext.Provider>
  );
};

export default SocialPage;
